<template>
  <GraphqlDataTable
    :query="query"
    :order-by="initialOrder"
    :column-black-list="['id']"
    :filter-black-list="['accounts']"
    :additional-query-variables="{ client: clientRelationId }"
    :search-placeholder="$t('Search your team…')"
    max-height-adjustment="15vh"
  >
    <template #no-results>
      <div v-if="assignedTotal === 0" data-test="client-team-owners-empty">
        <NoTrustedContactsFound :no-actions="!canManage">
          <template #main>
            {{ $t('No one from your team is assigned to this client yet') }}
          </template>
          <template #sub>
            <template v-if="canManage">
              {{ $t('Assign team members so the client shows up under “Your team” for them.') }}
            </template>
            <template v-else>{{ $t('Account owners and admins can assign colleagues.') }}</template>
          </template>
          <template #actions>
            <WdsButton primary data-test="client-team-owners-empty-manage" @click="emit('manage')">
              {{ $t('Manage team') }}
            </WdsButton>
          </template>
        </NoTrustedContactsFound>
      </div>
      <WdsText
        v-else-if="assignedTotal !== null"
        secondary
        class="tw-py-8"
        data-test="client-team-owners-no-match"
      >
        {{ $t('No one matches your search or filters') }}
      </WdsText>
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsButton, WdsText } from '@worksome/wds-components'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import NoTrustedContactsFound from '@/components/Contacts/CompanyFreelancer/NoTrustedContactsFound.vue'
import { graphql } from '@/gql'
import { SortOrder, ClientRelationOwnerOrderByColumn } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const query = graphql(`
  query ClientTeamOwnersTableQuery(
    $accounts: [ID!]
    $client: ID!
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [ClientRelationOwnerOrderByClauseInput!]
    $assignedAtDateRange: DateRangeInput
  ) {
    clientRelationOwners(
      accounts: $accounts
      client: $client
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      assignedAtDateRange: $assignedAtDateRange
    ) {
      data {
        id
        ...OwnerNameColumn
        ...OwnerEmailColumn
        ...OwnerPhoneColumn
        ...OwnerAssignedAtColumn
      }
    }
  }
`) as TypedQueryDocumentNode

defineProps<{
  clientRelationId: string
  canManage: boolean
  /** Everyone assigned, unfiltered; null until counted. Tells first use apart from no matches. */
  assignedTotal: number | null
}>()

const emit = defineEmits<{ (e: 'manage'): void }>()

const initialOrder = {
  field: ClientRelationOwnerOrderByColumn.Name,
  order: SortOrder.Asc,
}
</script>
